import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension, InputRule } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  DOMParser as ProseMirrorDOMParser,
  DOMSerializer,
  Slice,
} from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import Code from "@tiptap/extension-code";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { marked } from "marked";
import TurndownService from "turndown";
import "./TiptapEditor.css";

export interface TiptapEditorMethods {
  getValue: () => string;
  setValue: (value: string) => void;
  insertAtCursor: (text: string) => void;
  focus: () => void;
  switchToEditMode: () => void;
}

interface TiptapEditorProps {
  value?: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  maxLength?: number;
  readOnly?: boolean;
  hideToolbar?: boolean;
  previewMode?: "edit" | "preview" | "live";
  visibleDragBar?: boolean;
  dataColorMode?: "light" | "dark";
  onCursorChange?: (cursor: {
    selectionStart: number;
    selectionEnd: number;
  }) => void;
  onImageUpload?: (file: File) => Promise<string>;
}

const td = new TurndownService({ headingStyle: "atx", bulletListMarker: "-" });
td.keep(["u"]);

// Turndown escapes markdown-significant chars by default (e.g. `1.` -> `1\.`,
// `#` -> `\#`, `-` -> `\-`). Those backslashes get persisted into the stored
// prompt text and break downstream variable parsing (and show up as literal
// `\` if the content is viewed elsewhere). This is prompt text, not something
// that needs a lossless HTML<->MD round-trip, so disable escaping entirely and
// emit clean markdown.
td.escape = (str: string) => str;

const escapeTableCell = (text: string): string =>
  text
    .replace(/\|/g, "\\|")
    .replace(/\r?\n+/g, " ")
    .trim();

const tableToMarkdown = (table: HTMLTableElement): string => {
  const rowCells = (row: Element): HTMLTableCellElement[] =>
    Array.from(row.children).filter(
      (cell): cell is HTMLTableCellElement =>
        cell.tagName === "TH" || cell.tagName === "TD",
    );

  const allRows = Array.from(table.querySelectorAll("tr"));

  const theadRow = table.querySelector("thead tr");
  // The header is the thead row when present, otherwise the first row.
  const headerRow = theadRow ?? allRows[0];
  if (!headerRow) return "\n\n";

  const headerCells = rowCells(headerRow);

  // Body = every row except the header row itself (compare the <tr> elements,
  // not derived cell arrays — array identity never matched, which duplicated
  // the header row into the body).
  const bodyRows = allRows
    .filter((row) => row !== headerRow)
    .map(rowCells)
    .filter((cells) => cells.length > 0);

  if (!headerCells.length && !bodyRows.length) return "\n\n";

  const maxColumns = Math.max(
    headerCells.length,
    ...bodyRows.map((cells) => cells.length),
  );

  const normalizeRow = (cells: HTMLTableCellElement[]) => {
    const values = cells.map((cell) => escapeTableCell(cell.textContent ?? ""));
    while (values.length < maxColumns) values.push("");
    return values;
  };

  const markdownRows = [
    `| ${normalizeRow(headerCells).join(" | ")} |`,
    `| ${Array(maxColumns).fill("---").join(" | ")} |`,
    ...bodyRows.map((cells) => `| ${normalizeRow(cells).join(" | ")} |`),
  ];

  return `\n\n${markdownRows.join("\n")}\n\n`;
};

td.addRule("gfmTable", {
  filter: "table",
  replacement: (_, node) => tableToMarkdown(node as HTMLTableElement),
});

// TipTap renders task items as <li data-type="taskItem" data-checked="...">.
// Emit GFM `- [ ]` / `- [x]` so checkbox state survives the HTML→MD roundtrip.
// Narrow filter (only taskItem LIs) leaves plain bullet/ordered items untouched.
td.addRule("taskListItem", {
  filter: (node) =>
    node.nodeName === "LI" &&
    (node as HTMLElement).getAttribute("data-type") === "taskItem",
  replacement: (content, node) => {
    const checked =
      (node as HTMLElement).getAttribute("data-checked") === "true";
    const text = content.replace(/\r?\n+/g, " ").trim();
    return `- [${checked ? "x" : " "}] ${text}\n`;
  },
});

// Keep <hr> → `---` stable/idempotent (turndown's default emits `* * *`).
td.addRule("thematicBreak", {
  filter: "hr",
  replacement: () => "\n\n---\n\n",
});

const TABLE_ROW_RE = /^\s*\|.*\|\s*$/;
// A GFM delimiter row: only |, -, :, spaces — e.g. |---|---| or |:--:|--:|
const TABLE_DELIMITER_RE = /^\s*\|?[\s:|-]+\|?\s*$/;
const isDelimiterRow = (line: string): boolean =>
  TABLE_ROW_RE.test(line) &&
  TABLE_DELIMITER_RE.test(line) &&
  line.includes("-");

/**
 * GFM tables require their rows on consecutive lines. Stored prompts can come
 * back with blank lines between every row (e.g. `| a |\n\n|---|\n\n| b |`),
 * which `marked` then treats as separate paragraphs instead of a table.
 *
 * This rebuilds each table as a contiguous block: a delimiter row (`|---|`)
 * pulls its header (the pipe-row above it) and the following pipe-rows into one
 * block, with a single blank line kept BEFORE each table so consecutive tables
 * stay separate and don't merge into one. Non-table content is untouched.
 */
function repairMarkdownTables(md: string): string {
  // Pass 1: drop blank lines that sit strictly between two pipe-rows, so each
  // table's rows become contiguous (handles `| a |\n\n|---|\n\n| b |`).
  const lines = md.split("\n");
  const collapsed: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === "") {
      const prev = collapsed[collapsed.length - 1] ?? "";
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === "") j++;
      const next = j < lines.length ? lines[j] : "";
      if (TABLE_ROW_RE.test(prev) && TABLE_ROW_RE.test(next)) continue;
    }
    collapsed.push(line);
  }

  // Pass 2: two tables back-to-back are now ambiguous (`| Carol |` directly
  // followed by `| Header |` then `|---|`). A pipe-row that is the header of a
  // new table is the line whose NEXT line is a delimiter row. If the line
  // before such a header is also a pipe-row, the tables merged — re-insert one
  // blank line so `marked` parses them as two separate tables.
  const out: string[] = [];
  for (let i = 0; i < collapsed.length; i++) {
    const line = collapsed[i];
    const next = collapsed[i + 1] ?? "";
    const prevEmitted = out[out.length - 1] ?? "";
    const isHeaderOfNewTable =
      TABLE_ROW_RE.test(line) && !isDelimiterRow(line) && isDelimiterRow(next);
    if (
      isHeaderOfNewTable &&
      TABLE_ROW_RE.test(prevEmitted) &&
      prevEmitted.trim() !== ""
    ) {
      out.push("");
    }
    out.push(line);
  }

  return out.join("\n");
}

// A standalone thematic break line: only one repeated -, * or _ char (no pipes).
const THEMATIC_BREAK_RE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;

/**
 * marked treats `text\n---` as a setext H2 and DROPS the rule. Mirroring
 * MDEditorWrapper.normalizeHrTags: ensure a blank line precedes a standalone
 * thematic break so marked emits <hr>. Pipe-rows are skipped so this never
 * disturbs table delimiter rows handled by repairMarkdownTables.
 */
function repairMarkdownHr(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prev = out[out.length - 1] ?? "";
    const isBreak = THEMATIC_BREAK_RE.test(line) && !TABLE_ROW_RE.test(line);
    if (isBreak && prev.trim() !== "" && !TABLE_ROW_RE.test(prev)) out.push("");
    out.push(line);
  }
  return out.join("\n");
}

/**
 * marked emits task lists as `<li><input type="checkbox" [checked]> text</li>`.
 * TipTap's TaskItem.parseHTML matches `li[data-type="taskItem"]` (+ `data-checked`),
 * not raw checkboxes. Rewrite into the structure TipTap expects so MD checkboxes
 * hydrate as real task items.
 */
function taskListHtmlForTiptap(html: string): string {
  if (!html.includes('type="checkbox"')) return html;
  const doc = new window.DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("li").forEach((li) => {
    const input = li.querySelector(
      'input[type="checkbox"]',
    ) as HTMLInputElement | null;
    if (!input) return;
    const checked = input.hasAttribute("checked");
    input.remove();
    li.setAttribute("data-type", "taskItem");
    li.setAttribute("data-checked", checked ? "true" : "false");
    const parent = li.parentElement;
    if (parent && parent.tagName === "UL")
      parent.setAttribute("data-type", "taskList");
  });
  return doc.body.innerHTML;
}

function markdownToHtml(md: string): string {
  if (!md || md.trim() === "") return "";
  try {
    const repaired = repairMarkdownHr(repairMarkdownTables(md));
    const out = taskListHtmlForTiptap(
      marked.parse(repaired, {
        async: false,
        gfm: true,
        breaks: false,
      }) as string,
    );
    return out;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("[TiptapEditor] markdownToHtml THREW:", e);
    return `<p>${md}</p>`;
  }
}

function htmlToMarkdown(html: string): string {
  if (!html || html === "<p></p>") return "";
  try {
    return td.turndown(html);
  } catch {
    return html;
  }
}

const MARKDOWN_PASTE_KEY = new PluginKey("markdownPaste");

const looksLikeMarkdown = (text: string): boolean =>
  /(?:^|\n)#{1,6}\s|[*_]{1,3}[^*_\s][^*_]*[*_]{1,3}|(?:^|\n)\s*[-*+]\s|(?:^|\n)\s*\d+\.\s|(?:^|\n)>\s|```|\[.+?\]\(.+?\)|~~.+?~~|(?:^|\n)\s*\|?[\s:|-]*-[\s:|-]*\|[\s:|-]*(?:\n|$)/.test(
    text,
  );

// After Shift+Enter (hardBreak), typing `## ` doesn't trigger StarterKit's
// heading rule because its regex `^(#{1,6})\s$` anchors at the textblock
// start, not after the hardBreak. We match `## ` preceded by a hardBreak
// (rendered as `\n` in the input-rule text), then split the paragraph at
// the break and convert the new block to a heading.
const HeadingAfterHardBreak = Extension.create({
  name: "headingAfterHardBreak",
  addInputRules() {
    return [
      new InputRule({
        find: /\n(#{1,6})\s$/,
        handler: ({ state, range, match, chain }) => {
          const level = match[1].length as 1 | 2 | 3 | 4 | 5 | 6;
          const $from = state.selection.$from;

          // Find the hardBreak's absolute position by scanning the parent's
          // inline content for the last hardBreak before the cursor offset.
          let hardBreakAbsPos: number | null = null;
          $from.parent.forEach((node, offset) => {
            if (node.type.name === "hardBreak" && offset < $from.parentOffset) {
              hardBreakAbsPos = $from.start() + offset;
            }
          });
          if (hardBreakAbsPos === null) return null;

          // range.to is the cursor (after the trailing space). Delete from the
          // hardBreak through `## `, split the paragraph there, then promote
          // the new block to a heading. Doing it as one chain keeps positions
          // consistent via the chain's running tr.
          chain()
            .command(({ tr }) => {
              tr.delete(hardBreakAbsPos as number, range.to);
              return true;
            })
            .splitBlock()
            .setNode("heading", { level })
            .run();
        },
      }),
    ];
  },
});

const MarkdownPaste = Extension.create({
  name: "markdownPaste",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: MARKDOWN_PASTE_KEY,
        props: {
          handlePaste(view, event) {
            const text = event.clipboardData?.getData("text/plain") ?? "";
            if (!text || !looksLikeMarkdown(text)) return false;

            // marked pretty-prints with newlines BETWEEN tags. Those become
            // collapsible text nodes that ProseMirror drops, eating the real
            // space between inline elements ("Awesome screenshot" ->
            // "Awesomescreenshot"). Strip only newlines adjacent to a tag
            // boundary; spaces inside text content are untouched.
            const htmlString = markdownToHtml(text)
              .replace(/>\n+/g, ">")
              .replace(/\n+</g, "<");
            const dom = new window.DOMParser().parseFromString(
              htmlString,
              "text/html",
            );
            const pmDoc = ProseMirrorDOMParser.fromSchema(
              view.state.schema,
            ).parse(dom.body, { preserveWhitespace: false });
            const slice = new Slice(pmDoc.content, 0, 0);

            view.dispatch(
              view.state.tr.replaceSelection(slice).scrollIntoView(),
            );
            return true;
          },
        },
      }),
    ];
  },
});

function Toolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;

  const btn = (
    active: boolean,
    onClick: () => void,
    title: string,
    children: React.ReactNode,
  ) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`tiptap-toolbar-btn${active ? "is-active" : ""}`}
    >
      {children}
    </button>
  );

  return (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-section">
        {btn(
          editor.isActive("bold"),
          () => editor.chain().focus().toggleBold().run(),
          "Bold",
          <strong>B</strong>,
        )}
        {btn(
          editor.isActive("italic"),
          () => editor.chain().focus().toggleItalic().run(),
          "Italic",
          <em>I</em>,
        )}
        {btn(
          editor.isActive("underline"),
          () => editor.chain().focus().toggleUnderline().run(),
          "Underline",
          <u>U</u>,
        )}
        {btn(
          editor.isActive("strike"),
          () => editor.chain().focus().toggleStrike().run(),
          "Strikethrough",
          <s>S</s>,
        )}
        {btn(
          editor.isActive("code"),
          () => editor.chain().focus().toggleCode().run(),
          "Inline code",
          <code>{"`"}</code>,
        )}
      </div>

      <div className="tiptap-toolbar-divider" />

      <div className="tiptap-toolbar-section">
        {btn(
          editor.isActive("heading", { level: 1 }),
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          "Heading 1",
          <span>H1</span>,
        )}
        {btn(
          editor.isActive("heading", { level: 2 }),
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          "Heading 2",
          <span>H2</span>,
        )}
        {btn(
          editor.isActive("heading", { level: 3 }),
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          "Heading 3",
          <span>H3</span>,
        )}
      </div>

      <div className="tiptap-toolbar-divider" />

      <div className="tiptap-toolbar-section">
        {btn(
          editor.isActive("bulletList"),
          () => editor.chain().focus().toggleBulletList().run(),
          "Bullet list",
          <span>• List</span>,
        )}
        {btn(
          editor.isActive("orderedList"),
          () => editor.chain().focus().toggleOrderedList().run(),
          "Ordered list",
          <span>1. List</span>,
        )}
        {btn(
          editor.isActive("taskList"),
          () => editor.chain().focus().toggleTaskList().run(),
          "Task list",
          <span>☑ List</span>,
        )}
        {btn(
          editor.isActive("blockquote"),
          () => editor.chain().focus().toggleBlockquote().run(),
          "Blockquote",
          <span>"</span>,
        )}
        {btn(
          editor.isActive("codeBlock"),
          () => editor.chain().focus().toggleCodeBlock().run(),
          "Code block",
          <span>{"{}"}</span>,
        )}
        {btn(
          false,
          () => editor.chain().focus().setHorizontalRule().run(),
          "Horizontal rule",
          <span>―</span>,
        )}
      </div>

      <div className="tiptap-toolbar-divider" />

      <div className="tiptap-toolbar-section">
        {btn(
          false,
          () => editor.chain().focus().undo().run(),
          "Undo",
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 14L4 9l5-5" />
            <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
          </svg>,
        )}
        {btn(
          false,
          () => editor.chain().focus().redo().run(),
          "Redo",
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 14l5-5-5-5" />
            <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
          </svg>,
        )}
      </div>
    </div>
  );
}

const TiptapEditor = forwardRef<TiptapEditorMethods, TiptapEditorProps>(
  (props, ref) => {
    const {
      value = "",
      onChange,
      placeholder = "Type your content here...",
      height = 400,
      className = "",
      style,
      maxLength,
      readOnly = false,
      hideToolbar = false,
    } = props;

    const lastMarkdownRef = useRef<string>(value);
    const isSettingRef = useRef(false);
    const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
    const { onCursorChange } = props;
    const [shakeKey, setShakeKey] = useState(0);
    const [showLimitWarning, setShowLimitWarning] = useState(false);
    const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const triggerLimitFeedback = () => {
      setShakeKey((k) => k + 1);
      setShowLimitWarning(true);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      warningTimerRef.current = setTimeout(
        () => setShowLimitWarning(false),
        2500,
      );
    };
    useEffect(
      () => () => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      },
      [],
    );

    const editor = useEditor({
      extensions: [
        HeadingAfterHardBreak,
        // Disable StarterKit's bundled code mark and replace it with one whose
        // `excludes` is cleared. The default code mark has excludes:'_' (exclude
        // ALL other marks), so it can't combine with em/strong (italic + code).
        StarterKit.configure({ code: false }),
        Code.extend({ excludes: "" }),
        Underline,
        Link.configure({ openOnClick: false, autolink: true }),
        Image.configure({ inline: false }),
        Placeholder.configure({ placeholder }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        TaskList,
        TaskItem.configure({ nested: true }),
        MarkdownPaste,
      ],
      content: markdownToHtml(value),
      parseOptions: { preserveWhitespace: false },
      editable: !readOnly,
      editorProps: {
        clipboardTextSerializer: (slice, view) => {
          const serializer = DOMSerializer.fromSchema(view.state.schema);
          const div = document.createElement("div");
          div.appendChild(serializer.serializeFragment(slice.content));
          return htmlToMarkdown(div.innerHTML);
        },
        handlePaste: (view, event) => {
          if (!maxLength) return false;
          const clipboard = event.clipboardData;
          if (!clipboard) return false;
          const pasted =
            clipboard.getData("text/plain") ||
            clipboard.getData("text/html") ||
            "";
          if (!pasted) return false;
          const currentLen = lastMarkdownRef.current.length;
          const { from, to } = view.state.selection;
          const selectedLen = view.state.doc.textBetween(from, to).length;
          if (currentLen - selectedLen + pasted.length > maxLength) {
            event.preventDefault();
            triggerLimitFeedback();
            return true;
          }
          return false;
        },
      },
      onUpdate({ editor }) {
        if (isSettingRef.current) return;
        const html = editor.getHTML();
        const md = htmlToMarkdown(html);
        if (maxLength && md.length > maxLength) {
          triggerLimitFeedback();
          return;
        }
        lastMarkdownRef.current = md;
        onChange?.(md);
      },
      onSelectionUpdate({ editor }) {
        const { from, to } = editor.state.selection;
        savedSelectionRef.current = { from, to };
        editor.view.dispatch(editor.state.tr.scrollIntoView());
        if (!onCursorChange) return;
        onCursorChange({ selectionStart: from, selectionEnd: to });
      },
      onBlur({ editor }) {
        const { from, to } = editor.state.selection;
        savedSelectionRef.current = { from, to };
      },
    });

    useEffect(() => {
      if (!editor || isSettingRef.current) return;
      if (value === lastMarkdownRef.current) return;
      lastMarkdownRef.current = value;
      isSettingRef.current = true;
      const html = markdownToHtml(value);
      editor.commands.setContent(html, {
        parseOptions: { preserveWhitespace: false },
      });
      isSettingRef.current = false;
    }, [value, editor]);

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => lastMarkdownRef.current,
        setValue: (val: string) => {
          if (!editor) return;
          lastMarkdownRef.current = val;
          isSettingRef.current = true;
          editor.commands.setContent(markdownToHtml(val), {
            parseOptions: { preserveWhitespace: false },
          });
          isSettingRef.current = false;
          onChange?.(val);
        },
        insertAtCursor: (text: string) => {
          if (!editor) return;
          const saved = savedSelectionRef.current;
          const docSize = editor.state.doc.content.size;

          if (saved) {
            const from = Math.min(Math.max(saved.from, 1), docSize - 1);
            const to = Math.min(Math.max(saved.to, from), docSize - 1);

            editor.chain().focus().insertContentAt({ from, to }, text).run();
          } else {
            // No saved position — insert at end
            editor.chain().focus("end").insertContent(text).run();
          }

          // Save new cursor position after insert
          const { from: newFrom, to: newTo } = editor.state.selection;
          savedSelectionRef.current = { from: newFrom, to: newTo };

          const html = editor.getHTML();
          const md = htmlToMarkdown(html);
          lastMarkdownRef.current = md;
          onChange?.(md);
        },
        focus: () => editor?.commands.focus(),
        switchToEditMode: () => editor?.commands.focus(),
      }),
      [editor, onChange],
    );

    return (
      <div className="tiptap-editor-shell" style={{ height }}>
        <div
          key={shakeKey}
          className={`tiptap-editor-wrapper ${className} ${shakeKey > 0 ? "tiptap-shake" : ""}`}
          style={{ height: "100%", ...style }}
        >
          {!hideToolbar && <Toolbar editor={editor} />}
          <EditorContent
            editor={editor}
            className="tiptap-editor-content grey-scrollbar"
          />
        </div>
        {showLimitWarning && (
          <div className="tiptap-char-limit tiptap-char-limit--floating">
            <svg width="15" height="14" viewBox="0 0 15 14" fill="none">
              <path
                d="M14.3029 10.4994L9.35734 1.40823C8.56275 0.0705426 6.62581 0.0687653 5.83016 1.40823L0.884876 10.4994C0.0726023 11.8663 1.05602 13.5972 2.64806 13.5972H12.5393C14.13 13.5972 15.1151 11.8676 14.3029 10.4994ZM7.59375 11.9565C7.14154 11.9565 6.77343 11.5884 6.77343 11.1362C6.77343 10.684 7.14154 10.3159 7.59375 10.3159C8.04596 10.3159 8.41406 10.684 8.41406 11.1362C8.41406 11.5884 8.04596 11.9565 7.59375 11.9565ZM8.41406 8.67529C8.41406 9.1275 8.04596 9.4956 7.59375 9.4956C7.14154 9.4956 6.77343 9.1275 6.77343 8.67529V4.57373C6.77343 4.12152 7.14154 3.75342 7.59375 3.75342C8.04596 3.75342 8.41406 4.12152 8.41406 4.57373V8.67529Z"
                fill="#FB5F66"
              />
            </svg>
            Character limit reached
          </div>
        )}
      </div>
    );
  },
);

TiptapEditor.displayName = "TiptapEditor";
export default TiptapEditor;
